import { useNormalCase } from "../composables/UseCase";
import { UseDecodeToken } from "../composables/UseDecodeToken";
import { saveStorage, getStorage, clearStorage } from "../composables/UseStorage";
import type { AuthState } from "../types/auth/authState";
import type { LoginResponse } from "../types/auth/loginResponse";
import type { TokenData } from "../types/auth/tokenData";
import type { User } from "../types/auth/user";
import type { MenuItem } from "../types/menuItem";
import { useRuntimeConfig } from "nuxt/app";
import type { CurrentFilters } from "~/types/auth/CurrentFilters";
import type { Hierarchy } from "~/types/hierarchy";

const { toNormalCase } = useNormalCase();

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        authToken: null,
        isError: false,
        message: "Loading...",
        tokens: null,
        cognito_username: null,
        user: null,
        refreshOrganisationKey: 0,
        sessionExpiresAt: null,
        refreshTimer: null,
    }),
    getters: {
        UseAuthorization(): Object {
            return {
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                },
            };
        },
        getUser: (state: AuthState): User | null => {
            if (!state.user) {
                const store = getStorage(useRuntimeConfig().public.storageKeys.auth) as TokenData | null;
                if (store && store.user) {
                    state.user = store.user;
                    return state.user;
                }
            }
            return state.user;
        },
        getUserFirstName(state: AuthState): string | null {
            return state.user?.FirstName ?? null;
        },
        getUserFullName(state: AuthState): string | null {
            return state.user?.FirstName + ' ' + state.user?.LastName;
        },
        getUserRole(state: AuthState): string | null {
            let role = state.user?.Roles?.[0]?.Name ?? null;
            if (role) {
                return toNormalCase(role);
            }
            return null;
        },
        isSuperUser(state: AuthState): boolean {
            return state.user?.Roles?.some(role => toNormalCase(role?.Name) === 'Super') ?? false;
        },
        getSelectedOrganisation: (state: AuthState): CurrentFilters | null => {
            return state.user?.CurrentFilters ?? null;
        },
        getUserPortal: (state: AuthState): string=> {
            return state.user?.PortalType;
        },
    },
    actions: {
        setAuthToken(token: string): void {
            this.authToken = token;
        },
        login(tokens: TokenData): void {
            this.authToken = tokens.id_token;
            this.tokens = tokens;
            const decodedToken = UseDecodeToken(this.tokens.id_token);
            this.cognito_username = decodedToken["cognito:username"];
            const store = { ...this.tokens, cognito_username: this.cognito_username };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
            
            // Set up session expiry tracking and auto-refresh
            this.scheduleTokenRefresh(tokens.expires_in);
        },
        scheduleTokenRefresh(expiresIn: number): void {
            // Clear any existing timer
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
            }
            
            // Calculate expiry time (current time + expires_in seconds)
            this.sessionExpiresAt = Date.now() + (expiresIn * 1000);
            
            // Schedule refresh 5 minutes before expiry
            const refreshInMs = (expiresIn - 300) * 1000; // 300 seconds = 5 minutes
            
            if (refreshInMs > 0) {
                this.refreshTimer = setTimeout(() => {
                    this.refreshLogin();
                }, refreshInMs);
            }
        },
        scheduleTokenRefresh(expiresIn: number): void {
            // Clear any existing timer
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
            }
            
            // Calculate expiry time (current time + expires_in seconds)
            this.sessionExpiresAt = Date.now() + (expiresIn * 1000);
            
            // Schedule refresh 5 minutes before expiry
            const refreshInMs = (expiresIn - 300) * 1000; // 300 seconds = 5 minutes
            
            if (refreshInMs > 0) {
                this.refreshTimer = setTimeout(() => {
                    this.refreshLogin();
                }, refreshInMs);
            }
        },
        recordError(error: any): void {
            this.message = "Sorry, Accelerate appears to be offline. Please try again later.";
            this.isError = true;
            console.error(error);
        },
        getStorage(): void {
            const tokens = getStorage(useRuntimeConfig().public.storageKeys.auth) as TokenData | null;
            if (tokens) {
                this.tokens = tokens;
                this.authToken = tokens.id_token;
                this.user = tokens.user;
                // Schedule refresh based on token expiry if not already scheduled
                if (!this.refreshTimer && tokens.expires_in) {
                    const decodedToken = UseDecodeToken(tokens.id_token);
                    if (decodedToken?.exp) {
                        const expiresInSeconds = decodedToken.exp - Math.floor(Date.now() / 1000);
                        if (expiresInSeconds > 0) {
                            this.scheduleTokenRefresh(expiresInSeconds);
                        }
                    }
                }
            }
        },
        setNewFilters(filters: CurrentFilters): void {
            if (!this.user) {
                return;
            }
            this.user.CurrentFilters = filters;
            const store = { ...this.tokens, user: this.user };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
            this.refreshOrganisationKey++;
        },
        clear(): void {
            // Clear refresh timer
            if (this.refreshTimer) {
                clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
            this.sessionExpiresAt = null;
            
            this.tokens = null;
            this.authToken = null;
            this.user = null;
            this.cognito_username = null;
            clearStorage(useRuntimeConfig().public.storageKeys.auth);
            clearStorage(useRuntimeConfig().public.storageKeys.hierarchy);
            clearStorage(useRuntimeConfig().public.storageKeys.theme);
            //Clear out any additional nla-accelerate table keys
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key.startsWith("nla-accelerate-table-") || key.startsWith("nla-accelerate-")) {
                    localStorage.removeItem(key);
                }
            }
        },
        isLoginExpired(): boolean {
            this.getStorage();
            if (!this.tokens) {
                this.clear();
                return true;
            }
            const decodedToken = UseDecodeToken(this.tokens.id_token);
            if (!decodedToken) {
                this.clear();
                return true;
            }
            if (decodedToken.exp < Date.now() / 1000) {
                this.clear();
                return true;
            }
            return false;
        },
        saveUser(user: User): void {
            this.user = user;
            const store = { user: user, ...this.tokens };
            saveStorage(useRuntimeConfig().public.storageKeys.auth, store);
        },
        async getUserDetails(): Promise<void> {
            this.message = "Retrieving profile...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/details`, {
                    method: "GET",
                    ...this.UseAuthorization,
                });
                const data = await response.json();
                if (response.ok) {
                    this.saveUser(data as User);
                } else {
                    this.recordError(data);
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async getHierarchyDetail(): Promise<void> {
            this.message = "Retrieving organisation details...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/accessible-hierarchy`, {
                    method: "GET",
                    ...this.UseAuthorization,
                });
                const data: Hierarchy[] = await response.json();
                if (response.ok) {
                    saveStorage(useRuntimeConfig().public.storageKeys.hierarchy, data, false);
                } else {
                    this.recordError("Failed to retrieve organisation details");
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async getHostedUrl(): Promise<LoginResponse | null> {
            this.message = "Checking login status...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/public/auth/cognito/login`);
                const data = await response.json();
                if (response.status === 200 && data.url) {
                    this.message = "Redirecting to login...";
                    return { url: data.url, external: true };
                } else {
                    this.recordError(data);
                    return null;
                }
            } catch (error) {
                this.recordError(error);
                return null;
            }
        },
        async getAccessibleMenus(): Promise<void> {
            this.message = "Preparing menu...";

            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/accessible-menus`, {
                    method: "GET",
                    ...useAuthStore().UseAuthorization,
                });

                if (response.ok) {
                    const data: MenuItem[] = await response.json();

                    const activeMenus = data.filter((item: MenuItem) => item.IsActive) as MenuItem[];

                    const groupedMenus = activeMenus.reduce((acc: { [key: string]: MenuItem[] }, item: MenuItem) => {
                        const menuGroup = item.MenuGroup ?? 'default';
                        if (!acc[menuGroup]) {
                            acc[menuGroup] = [];
                        }
                        acc[menuGroup].push(item);
                        return acc;
                    }, {});

                    const orderedMenus = Object.keys(groupedMenus).sort((a, b) => Number(a) - Number(b)).reduce((acc: { [key: string]: MenuItem[] }, key: string) => {
                        acc[key] = groupedMenus[key];
                        return acc;
                    }, {});

                    saveStorage(useRuntimeConfig().public.storageKeys.menu, orderedMenus, false);
                } else {
                    this.message = "Failed to prepare menu";
                }
            } catch (error) {
                this.recordError(error);
            }
        },
        async postExchangeCodeForToken(code: string): Promise<LoginResponse | null> {
            this.message = "Validating Login...";
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/public/auth/cognito/exchange`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ cognito_code: code }),
                });

                const data = await response.json();
                if (response.ok) {
                    this.login(data.tokens as TokenData);
                    await this.getUserDetails();
                    this.message = `Welcome to Accelerate ${this.user?.FirstName ? ", " + this.user.FirstName : ""}.`;
                    await this.getHierarchyDetail();
                    await this.getAccessibleMenus();
                    const url = this.user?.PortalType === 'dealer' ? '/dealer/dashboard' : '/dashboard'
                    return { url: url, external: false };
                } else {
                    this.recordError(data);
                    return null;
                }
            } catch (error) {
                this.recordError(error);
                return null;
            }
        },
        async refreshLogin(): Promise<boolean> {
            this.message = null;
            this.getStorage();
            if (!this.tokens) {
                this.message = "Session expired, please log in again.";
                return false;
            }
            
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/auth/cognito/refresh`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        username: this.tokens["cognito_username"],
                    }),
                });

                const data = await response.json();
                if (response.ok) {
                    this.login(data.tokens as TokenData);
                    return true;
                } else {
                    this.clear();
                    this.message = "Session expired, please log in again.";
                    return false;
                }
            } catch (error) {
                this.recordError(error);
                return false;
            }
        },
        async signOut(): Promise<boolean> {
            try {
                const config = useRuntimeConfig();
                const response = await fetch(`${config.public.apiBase}/api/user/logout`, {
                    method: "POST",
                    ...this.UseAuthorization,
                });

                if (response.ok) {
                    this.clear();
                    return true;
                } else {
                    this.clear();
                    console.error("Error during sign out, but session was cleared.");
                    return false;
                }
            } catch (error) {
                this.recordError(error);
                this.clear();
                return false;
            }
        },
    },
});
